/**
 * Reporte a Equifax (buró de crédito, entrega quincenal según la ley 172-13).
 *
 * El backend (/api/reporte_equifax) llena la plantilla oficial de Equifax
 * ("ESTRUCTURA PARA REPORTAR A CLIENTES — PRODUCTO PRÉSTAMO") con los
 * préstamos activos, incobrables y los cancelados recientes: una fila por
 * deudor y otra por codeudor (fiador). Aquí se elige la fecha de corte y el
 * código de afiliado, se revisa el resumen y las advertencias de datos
 * incompletos, y se descarga el Excel para enviarlo a Equifax.
 *
 * Es el reporte de toda la cartera: solo para usuarios con acceso a todas
 * las sucursales (el backend lo vuelve a exigir).
 */
import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, StatCard, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, usePerfilSucursal } from "lib/api";

const CLAVE_AFILIADO = "equifax.afiliado";

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const hoyISO = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const leerAfiliado = () => {
  try {
    return localStorage.getItem(CLAVE_AFILIADO) || "";
  } catch {
    return "";
  }
};

const guardarAfiliado = (v) => {
  try {
    localStorage.setItem(CLAVE_AFILIADO, v);
  } catch {
    // sin almacenamiento: se vuelve a digitar la próxima vez
  }
};

const ESTATUS = {
  N: { label: "Normal", color: FP.color.successDark, bg: FP.color.successSoft },
  A: { label: "Atraso", color: "#92400E", bg: FP.color.warningSoft },
  L: { label: "Legal", color: FP.color.danger, bg: FP.color.dangerSoft },
  C: { label: "Castigado", color: FP.color.danger, bg: FP.color.dangerSoft },
  S: { label: "Saldado", color: FP.color.textSecondary, bg: FP.color.surfaceAlt },
};

const fecha8 = (v) => {
  const s = String(v || "");
  return s.length === 8 ? `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}` : "";
};

function descargarBase64(base64, nombre) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ReporteEquifax() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { esadmin } = usePerfilSucursal();

  const [fechacorte, setFechacorte] = useState(hoyISO);
  const [afiliado, setAfiliado] = useState(leerAfiliado);
  const [diascancelados, setDiascancelados] = useState("45");
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const generar = async () => {
    if (!fechacorte) {
      showAlert({ message: "Indique la fecha de corte", color: "warning" });
      return;
    }
    setCargando(true);
    try {
      const { ok, data } = await post("/api/reporte_equifax", {
        fechacorte,
        afiliado: afiliado.trim(),
        diascancelados: Number(diascancelados) || 45,
      });
      if (ok) {
        guardarAfiliado(afiliado.trim());
        setResultado(data);
      } else {
        setResultado(null);
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  const filas = useMemo(
    () => (resultado?.filas || []).map((f, i) => ({ id: i, ...f })),
    [resultado]
  );
  const r = resultado?.resumen;

  const columns = useMemo(
    () => [
      { field: "cuenta", headerName: "Cuenta", width: 90 },
      {
        field: "relacion",
        headerName: "Relación",
        width: 100,
        valueFormatter: (value) => (Number(value) === 2 ? "Codeudor" : "Deudor"),
      },
      {
        field: "nombres",
        headerName: "Nombre",
        flex: 1,
        minWidth: 200,
        valueGetter: (_v, row) => `${row.nombres || ""} ${row.apellidos || ""}`.trim(),
      },
      { field: "documento", headerName: "Documento", width: 130 },
      {
        field: "estatus",
        headerName: "Estatus",
        width: 110,
        renderCell: ({ value }) => {
          const e = ESTATUS[value] || ESTATUS.N;
          return (
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1.2,
                fontSize: "0.74rem",
                fontWeight: 700,
                bgcolor: e.bg,
                color: e.color,
              }}
            >
              {e.label}
            </Box>
          );
        },
      },
      { field: "tipoprestamo", headerName: "Tipo", width: 110 },
      {
        field: "montoadeudado",
        headerName: "Adeudado",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => fmtMoney(value),
      },
      {
        field: "totalatraso",
        headerName: "Total atraso",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value === "" || value == null ? "" : fmtMoney(value)),
      },
      { field: "diasatraso", headerName: "Días", width: 70, align: "right", headerAlign: "right" },
      {
        field: "fechaultimopago",
        headerName: "Último pago",
        width: 110,
        valueFormatter: (value) => fecha8(value),
      },
    ],
    []
  );

  if (!esadmin) {
    return (
      <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
        <PageHeader title="Reporte a Equifax" subtitle="Buró de crédito" />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Alert severity="info">
            El reporte a Equifax incluye toda la cartera; solo lo puede generar un usuario con
            acceso a todas las sucursales.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Reporte a Equifax"
        subtitle="Préstamos para el buró de crédito con la estructura de Equifax (entrega quincenal)"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.soft,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de corte"
                  value={fechacorte}
                  onChange={(e) => setFechacorte(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Equifax recibe los días 15 y 30"
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Código de afiliado"
                  value={afiliado}
                  onChange={(e) => setAfiliado(e.target.value.toUpperCase())}
                  helperText="El que asignó Equifax (ej. AF000022333)"
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Incluir cancelados de los últimos"
                  value={diascancelados}
                  onChange={(e) => setDiascancelados(e.target.value)}
                  inputProps={{ min: 1, max: 365 }}
                  helperText="Días. Van como Saldado con balance en cero"
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={1.2}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={
                      cargando ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PlayArrowRoundedIcon />
                      )
                    }
                    disabled={cargando}
                    onClick={generar}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 1.8,
                      boxShadow: "none",
                      bgcolor: FP.color.navy,
                      color: "#fff",
                      height: 46,
                      "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none" },
                    }}
                  >
                    Generar
                  </Button>
                  <Button
                    fullWidth
                    startIcon={<FileDownloadRoundedIcon />}
                    disabled={!resultado?.archivo}
                    onClick={() => descargarBase64(resultado.archivo, resultado.nombre)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 1.8,
                      height: 46,
                      color: FP.color.successDark,
                      border: `1px solid ${FP.color.successDark}`,
                    }}
                  >
                    Descargar Excel
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {r && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Préstamos reportados"
                  value={r.prestamos}
                  icon={AccountBalanceRoundedIcon}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard label="Codeudores" value={r.codeudores} icon={GroupsRoundedIcon} />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Monto adeudado"
                  value={`RD$ ${fmtMoney(r.adeudado)}`}
                  icon={PaidRoundedIcon}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Total en atraso"
                  value={`RD$ ${fmtMoney(r.atraso)}`}
                  icon={WarningAmberRoundedIcon}
                  accent={FP.color.danger}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {Object.entries(ESTATUS).map(([k, e]) => (
                <Box
                  key={k}
                  sx={{
                    px: 1.4,
                    py: 0.6,
                    borderRadius: 1.6,
                    bgcolor: e.bg,
                    color: e.color,
                    fontWeight: 700,
                    fontSize: "0.82rem",
                  }}
                >
                  {e.label}: {r[k] || 0}
                </Box>
              ))}
            </Stack>

            {(resultado.advertencias || []).length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.86rem", mb: 0.5 }}>
                  {resultado.advertencias.length} dato
                  {resultado.advertencias.length === 1 ? "" : "s"} por corregir antes de enviar
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.2, fontSize: "0.82rem" }}>
                  {resultado.advertencias.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </Box>
              </Alert>
            )}

            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${FP.color.border}`,
                boxShadow: FP.shadow.soft,
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2.5, py: 1.8, borderBottom: `1px solid ${FP.color.border}` }}>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  {filas.length} filas · {resultado.entidad} · corte {resultado.fechacorte}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={filas}
                  columns={columns}
                  rowHeight={44}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  sx={fpDataGridSx}
                  disableRowSelectionOnClick
                />
              </Box>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}
